import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CalendarDays, MapPin, Video, Clock, Users, Tag, Percent, List, Grid3X3, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, isFuture, isPast, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { SEOHead } from '@/components/seo/SEOHead';
import { Skeleton } from '@/components/ui/skeleton';
import { useDisplaySettings } from '@/hooks/useDisplaySettings';

type ViewMode = 'list' | 'calendar';

export default function EventsPage() {
  const { settings } = useDisplaySettings();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['public-events'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('events')
        .select('*')
        .eq('is_published', true)
        .order('start_date', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: promotions = [] } = useQuery({
    queryKey: ['public-events-promotions'],
    enabled: settings.show_promotions_on_events,
    queryFn: async () => {
      const { data, error } = await pmsFrom('promotions')
        .select('*')
        .eq('is_active', true)
        .not('start_date', 'is', null)
        .not('end_date', 'is', null)
        .order('start_date', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const upcoming = events.filter(e => isFuture(new Date(e.end_date || e.start_date)));
  const past = events.filter(e => isPast(new Date(e.end_date || e.start_date)));

  const promoEntries = settings.show_promotions_on_events
    ? promotions.filter(p => p.end_date && isFuture(parseISO(p.end_date)))
    : [];

  return (
    <>
      <SEOHead title="Events" description="Upcoming events, workshops, and community gatherings." />
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-4xl font-bold mb-1">Events</h1>
            <p className="text-muted-foreground">Join us at our upcoming events and workshops</p>
          </div>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-1.5"
            >
              <List className="h-4 w-4" /> List
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="gap-1.5"
            >
              <Grid3X3 className="h-4 w-4" /> Calendar
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        ) : viewMode === 'list' ? (
          <ListView upcoming={upcoming} past={past} promoEntries={promoEntries} />
        ) : (
          <CalendarView
            events={events}
            promoEntries={promoEntries}
            calendarMonth={calendarMonth}
            onPrevMonth={() => setCalendarMonth(m => subMonths(m, 1))}
            onNextMonth={() => setCalendarMonth(m => addMonths(m, 1))}
            onToday={() => setCalendarMonth(new Date())}
          />
        )}
      </div>
    </>
  );
}

/* ---------- List View ---------- */
function ListView({ upcoming, past, promoEntries }: { upcoming: any[]; past: any[]; promoEntries: any[] }) {
  return (
    <>
      {promoEntries.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2 text-primary">
            <Tag className="h-4 w-4" /> Current Promotions
          </h2>
          <div className="grid gap-3">
            {promoEntries.map(promo => (
              <PromoCard key={promo.id} promo={promo} />
            ))}
          </div>
        </section>
      )}

      {upcoming.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4">Upcoming Events</h2>
          <div className="grid gap-4">
            {upcoming.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {past.length > 0 && (
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-muted-foreground">Past Events</h2>
          <div className="grid gap-4 opacity-75">
            {past.map(event => (
              <EventCard key={event.id} event={event} isPast />
            ))}
          </div>
        </section>
      )}

      {upcoming.length === 0 && past.length === 0 && promoEntries.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No events scheduled at this time.</p>
          <p className="text-sm mt-1">Check back soon for upcoming events!</p>
        </div>
      )}
    </>
  );
}

/* ---------- Calendar View ---------- */
function CalendarView({
  events,
  promoEntries,
  calendarMonth,
  onPrevMonth,
  onNextMonth,
  onToday,
}: {
  events: any[];
  promoEntries: any[];
  calendarMonth: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
}) {
  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start with empty cells for day of week alignment
  const startDayOfWeek = getDay(monthStart); // 0 = Sunday

  const getEventsForDay = (day: Date) =>
    events.filter(e => isSameDay(parseISO(e.start_date), day));

  const getPromosForDay = (day: Date) =>
    promoEntries.filter(p => {
      const start = parseISO(p.start_date);
      const end = parseISO(p.end_date);
      return day >= start && day <= end;
    });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div>
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={onPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold min-w-[180px] text-center">
            {format(calendarMonth, 'MMMM yyyy')}
          </h2>
          <Button variant="outline" size="icon" onClick={onNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={onToday}>
          Today
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-xl overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-muted">
          {weekDays.map(d => (
            <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground border-b">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {/* Empty padding cells */}
          {Array.from({ length: startDayOfWeek }).map((_, i) => (
            <div key={`pad-${i}`} className="min-h-[100px] border-b border-r bg-muted/20" />
          ))}

          {days.map(day => {
            const dayEvents = getEventsForDay(day);
            const dayPromos = getPromosForDay(day);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[100px] border-b border-r p-1.5 ${
                  isToday ? 'bg-primary/5' : ''
                }`}
              >
                <div className={`text-xs font-medium mb-1 ${
                  isToday ? 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center' : 'text-muted-foreground'
                }`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.map(event => (
                    <Link key={event.id} to={`/events/${event.slug}`}>
                      <div className="text-[10px] leading-tight bg-primary/10 text-primary rounded px-1 py-0.5 truncate hover:bg-primary/20 transition-colors cursor-pointer">
                        {event.title}
                      </div>
                    </Link>
                  ))}
                  {dayPromos.map(promo => (
                    <div key={promo.id} className="text-[10px] leading-tight bg-accent/50 text-accent-foreground rounded px-1 py-0.5 truncate">
                      <Percent className="h-2.5 w-2.5 inline mr-0.5" />
                      {promo.title}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- Cards ---------- */
function PromoCard({ promo }: { promo: any }) {
  const startDate = promo.start_date ? parseISO(promo.start_date) : null;
  const endDate = promo.end_date ? parseISO(promo.end_date) : null;

  return (
    <Card className="border-primary/20 bg-primary/5 hover:shadow-sm transition-shadow">
      <CardContent className="p-0 flex">
        <div className="w-20 shrink-0 flex flex-col items-center justify-center bg-primary/10 border-r border-primary/20 text-center py-4">
          <Percent className="h-5 w-5 text-primary mb-1" />
          {startDate && (
            <>
              <span className="text-xs font-medium text-primary uppercase">{format(startDate, 'MMM')}</span>
              <span className="text-lg font-bold">{format(startDate, 'd')}</span>
            </>
          )}
        </div>
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-base">{promo.title}</h3>
              {promo.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{promo.description}</p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Badge className="bg-primary text-primary-foreground">
                {promo.discount_type === 'percent' ? `${promo.discount_value}% Off` : promo.discount_type === 'fixed' ? `$${promo.discount_value} Off` : 'Special Offer'}
              </Badge>
              {promo.promo_code && (
                <Badge variant="outline" className="font-mono text-xs">{promo.promo_code}</Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {startDate && endDate && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(startDate, 'MMM d')} – {format(endDate, 'MMM d, yyyy')}
              </span>
            )}
            {promo.banner_text && (
              <span className="text-primary font-medium">{promo.banner_text}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EventCard({ event, isPast = false }: { event: any; isPast?: boolean }) {
  return (
    <Link to={`/events/${event.slug}`}>
      <Card className="hover:shadow-md transition-shadow group overflow-hidden">
        <CardContent className="p-0 flex">
          <div className="w-20 shrink-0 flex flex-col items-center justify-center bg-primary/5 border-r text-center py-4">
            <span className="text-xs font-medium text-primary uppercase">{format(new Date(event.start_date), 'MMM')}</span>
            <span className="text-2xl font-bold">{format(new Date(event.start_date), 'd')}</span>
            <span className="text-xs text-muted-foreground">{format(new Date(event.start_date), 'yyyy')}</span>
          </div>
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">{event.title}</h3>
                {event.excerpt && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{event.excerpt}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {event.is_featured && <Badge>Featured</Badge>}
                {event.is_virtual && <Badge variant="outline"><Video className="h-3 w-3 mr-1" />Virtual</Badge>}
                {event.is_free && event.registration_enabled && <Badge variant="secondary">Free</Badge>}
                {!event.is_free && event.ticket_price > 0 && <Badge variant="outline">${event.ticket_price}</Badge>}
                {isPast && <Badge variant="secondary">Past</Badge>}
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {event.all_day ? 'All Day' : format(new Date(event.start_date), 'h:mm a')}
                {event.end_date && ` – ${format(new Date(event.end_date), 'h:mm a')}`}
              </span>
              {event.venue_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {event.venue_name}{event.venue_city ? `, ${event.venue_city}` : ''}
                </span>
              )}
              {event.registration_enabled && event.max_attendees && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" /> {event.registration_count}/{event.max_attendees} spots
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
